import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { revalidateLogic, useForm, useFormErrors, useStore } from '@eureca/ui/forms';
import { z } from 'zod';
import {
  JobEditRequirementsPage as JobEditRequirementsPageComponent,
  type DiversityCriterionId,
  type JobEditRequirementsValues,
  type SelectedDisabilityCriterion,
  type SelectedSkill,
} from './JobEditRequirementsPage.component';
import { JobEditRequirementsLoadingFallback } from './JobEditRequirementsLoadingFallback';
import { JobEditRequirementsErrorFallback } from './JobEditRequirementsErrorFallback';
import {
  COMPETENCIA_PRESETS,
  DIFERENCIAL_PRESETS,
  emptyValues,
} from './JobEditRequirementsPage.mock';
import { SkillDialog, type SkillDialogMode, type SkillSubmission, levelLabel } from './SkillDialog';
import { DisabilityDialog, type DisabilitySelection } from './DisabilityDialog';
import { buildDisabilityPillLabel } from './disabilities-seed';
import { JobFetchError, jobByIdQueryOptions } from '@/features/jobs/job-shared/job-by-id-query';
import { isJobDetailReadOnly } from '@/features/jobs/job-shared/job-status';
import { JobMutationError, usePatchJob } from '@/features/jobs/job-shared/job-edit-mutation';
import { DEFAULT_JOBS_SEARCH } from '@/features/jobs/jobs-list/jobs-search.schema';
import { useJobCompanyLogo } from '@/features/jobs/job-shared/use-job-company-logo';

type ProficiencyLevel = 'basico' | 'intermediario' | 'avancado';
type DisabilityType =
  | 'auditiva'
  | 'fisica'
  | 'intelectual'
  | 'visual'
  | 'multipla'
  | 'tea'
  | 'outra';

// EU-163: cap do Core (`competencias.max(50)` e `diferenciais.max(50)`).
export const MAX_SKILLS_PER_FIELD = 50;

function makeSkillId(): string {
  return `skill-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function toSelectedSkill(s: { name: string; level: ProficiencyLevel }): SelectedSkill {
  return {
    id: makeSkillId(),
    name: s.name,
    level: s.level,
    label: `${s.name} - ${levelLabel(s.level)}`,
  };
}

// EU-163 schema shape-only. Cross-field de idade vive em `computeAgeErrors`
// (não no Zod) porque (a) depende de `contractType` que vem de outra subseção
// e (b) o gate omite os campos do PATCH ao invés de bloquear o submit
// inteiro — outros campos seguem salvando mesmo com faixa de idade inválida.
const skillSchema = z.object({
  id: z.string(),
  name: z.string(),
  level: z.string(),
  label: z.string(),
});

const diversitySchema = z.union([
  z.literal('mulheres'),
  z.literal('mulheres-negras'),
  z.literal('transexuais'),
  z.literal('lgbtqiap'),
  z.literal('amarelas'),
  z.literal('indigenas'),
  z.literal('pardas'),
  z.literal('pretas'),
  z.literal('pessoas-com-deficiencia'),
  z.literal('50-plus'),
]);

const disabilityDetailsSchema = z.object({
  selectedIds: z.array(z.string()),
  otherText: z.string(),
  label: z.string(),
});

const schema = z.object({
  ageMin: z.string(),
  ageMax: z.string(),
  selectedCompetencias: z.array(skillSchema),
  selectedDiferenciais: z.array(skillSchema),
  selectedDiversity: z.array(diversitySchema),
  disabilityDetails: disabilityDetailsSchema.nullable(),
});

interface PatchPayload {
  ageMin?: number | null;
  ageMax?: number | null;
  competencias?: { name: string; level: ProficiencyLevel }[];
  diferenciais?: { name: string; level: ProficiencyLevel }[];
  diversityCriteria?: DiversityCriterionId[];
  disabilityDetails?: { selectedIds: DisabilityType[]; otherText: string } | null;
}

// EU-163 RN-03 — limite legal do contrato Jovem Aprendiz no Brasil
// (Lei 10.097/2000 + Decreto 5.598/2005): entre 14 e 24 anos. Outros tipos
// de contratação não têm cap superior obrigatório (estágio pode ir além).
const APPRENTICE_MAX_AGE = 24;

// EU-163 RN-02 — idade mínima por tipo de contratação:
//   - jovem-aprendiz: 14 (Lei 10.097/2000)
//   - estagio / estagio-tecnico: 16 (Lei 11.788/2008 — estagiário precisa
//     estar matriculado em ensino regular; 16 é o piso prático no Brasil)
//   - trainee: 18 (maioridade — programas de trainee exigem ensino superior
//     completo ou em conclusão)
// Quando `contractType` não está setado ainda, cai no piso legal de 14 anos.
function minAgeForContract(contractType: string | null | undefined): {
  min: number;
  label: string | null;
} {
  switch (contractType) {
    case 'jovem-aprendiz':
      return { min: 14, label: 'Aprendiz' };
    case 'estagio':
      return { min: 16, label: 'Estágio' };
    case 'estagio-tecnico':
      return { min: 16, label: 'Estágio técnico' };
    case 'trainee':
      return { min: 18, label: 'Trainee' };
    default:
      return { min: 14, label: null };
  }
}

/**
 * Computa erros de idade combinando regras hard do schema (`ageMax > ageMin`)
 * com a RN-02 (idade mínima por tipo de contratação) e RN-03
 * (`jovem-aprendiz` exige `ageMax ≤ 24`). `contractType` vem da subseção
 * Condições de Trabalho (subseção irmã); o consultor pode editar essa
 * subseção e quebrar uma vaga que já estava válida em Requisitos — daí
 * as regras serem cross-section e re-validadas aqui.
 *
 * Retorna `{ ageMin?, ageMax? }` com mensagens prontas para serem injetadas
 * em `errors` do componente. `undefined` significa "sem erro".
 */
function computeAgeErrors(
  values: JobEditRequirementsValues,
  contractType: string | null | undefined
): { ageMin?: string; ageMax?: string } {
  const min = values.ageMin ? Number(values.ageMin) : null;
  const max = values.ageMax ? Number(values.ageMax) : null;
  const result: { ageMin?: string; ageMax?: string } = {};
  const { min: minRequired, label } = minAgeForContract(contractType);
  // RN-02 — idade mínima por tipo. Quando há tipo reconhecido, mensagem
  // específica; sem tipo, mensagem genérica com o piso legal de 14.
  if (values.ageMin && (!Number.isFinite(min) || (min as number) < minRequired)) {
    result.ageMin = label
      ? `Idade mínima para ${label} é ${minRequired} anos`
      : 'A idade mínima é 14 anos';
  }
  // ageMax tem piso legal de 14 (não faz sentido idade máxima < 14 em
  // nenhum contrato). O cap superior por tipo é RN-03 logo abaixo.
  if (values.ageMax && (!Number.isFinite(max) || (max as number) < 14)) {
    result.ageMax = 'A idade mínima é 14 anos';
  }
  if (
    values.ageMin &&
    values.ageMax &&
    Number.isFinite(min) &&
    Number.isFinite(max) &&
    (max as number) <= (min as number)
  ) {
    result.ageMax = 'Este valor deve ser maior que o mínimo digitado anteriormente.';
  }
  // RN-03 — Jovem Aprendiz: idade máxima ≤ 24. Aplica APENAS quando
  // `contractType === 'jovem-aprendiz'`; outros contratos seguem o gate
  // genérico acima sem cap superior.
  if (
    contractType === 'jovem-aprendiz' &&
    values.ageMax &&
    Number.isFinite(max) &&
    (max as number) > APPRENTICE_MAX_AGE
  ) {
    result.ageMax = `Idade máxima para Aprendiz é ${APPRENTICE_MAX_AGE} anos`;
  }
  return result;
}

// EU-163 buildPayload: descarta `id`/`label` das skills (client-only),
// converte idade vazia → null, e aplica gate cross-field específico:
// quando `ageMin/ageMax` é inválido (< 14, max ≤ min com ambos preenchidos,
// ou jovem-aprendiz com max > 24), OMITIR os 2 campos do payload (PATCH
// style — Core mantém estado antigo). Outros campos (`competencias`,
// `diferenciais`, `diversityCriteria`, `disabilityDetails`) seguem salvando
// normalmente. Paridade com EU-161 faixa salarial.
function buildPayload(
  values: JobEditRequirementsValues,
  contractType: string | null | undefined
): PatchPayload {
  const min = values.ageMin ? Number(values.ageMin) : null;
  const max = values.ageMax ? Number(values.ageMax) : null;
  const ageErrors = computeAgeErrors(values, contractType);
  const ageInvalid = Boolean(ageErrors.ageMin || ageErrors.ageMax);

  const payload: PatchPayload = {
    competencias: values.selectedCompetencias.map((s) => ({
      name: s.name,
      level: s.level as ProficiencyLevel,
    })),
    diferenciais: values.selectedDiferenciais.map((s) => ({
      name: s.name,
      level: s.level as ProficiencyLevel,
    })),
    diversityCriteria: [...values.selectedDiversity],
    disabilityDetails: values.disabilityDetails
      ? {
          selectedIds: values.disabilityDetails.selectedIds as DisabilityType[],
          otherText: values.disabilityDetails.otherText,
        }
      : null,
  };
  if (!ageInvalid) {
    payload.ageMin = values.ageMin ? (min as number) : null;
    payload.ageMax = values.ageMax ? (max as number) : null;
  }
  return payload;
}

export default function JobEditRequirementsPage() {
  const navigate = useNavigate();
  const { jobId } = useParams({
    from: '/vagas/$jobId/editar/requisitos',
  });

  const jobQuery = useQuery(jobByIdQueryOptions(jobId));
  const patchJob = usePatchJob(jobId);
  const [hasHydrated, setHasHydrated] = useState(false);

  const job = jobQuery.data;
  // EU-569 — vaga Encerrada/Cancelada: seção em modo somente visualização.
  const readOnly = isJobDetailReadOnly(job);
  const jobName = job?.name ?? '';
  const { companyLogoUrl, companyName } = useJobCompanyLogo(job?.programId);

  const [skillDialogMode, setSkillDialogMode] = useState<SkillDialogMode | null>(null);
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [disabilityDialogOpen, setDisabilityDialogOpen] = useState(false);

  // Cap errors separados por field (FR-092). `undefined` quando não atingido.
  const [competenciaCapError, setCompetenciaCapError] = useState<string | undefined>();
  const [diferencialCapError, setDiferencialCapError] = useState<string | undefined>();

  // `contractType` vem da subseção irmã (`/condicoes-de-trabalho`). Quando o
  // consultor edita lá, o `setQueryData` da `usePatchJob.onSuccess`
  // atualiza o cache do `jobByIdQueryKey`, e ao voltar para Requisitos o
  // `useQuery` retorna o valor atualizado. Daí ler direto de `job` é OK.
  const contractType = job?.contractType ?? null;

  async function persist(values: JobEditRequirementsValues) {
    try {
      // `buildPayload` usa o `DiversityCriterionId` local (já com
      // `mulheres-negras` + `50-plus`). O enum gerado do recruiter-api-sdk
      // ainda não inclui esses dois tokens (regen pende de backend live), então
      // o cast pro tipo de variáveis da mutation mantém o typecheck verde; o
      // runtime envia os tokens corretos, que o BFF agora aceita.
      await patchJob.mutateAsync(
        buildPayload(values, contractType) as Parameters<typeof patchJob.mutateAsync>[0]
      );
      toast('Alterações salvas com sucesso', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      void navigate({
        to: '/vagas/$jobId',
        params: { jobId },
      });
    } catch (err) {
      const status = err instanceof JobMutationError ? err.status : 0;
      if (status === 404) {
        toast.error('Vaga não encontrada.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para editar essa vaga.');
      } else {
        toast.error('Não foi possível salvar as alterações. Tente novamente.');
      }
    }
  }

  const form = useForm({
    defaultValues: emptyValues,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      await persist(value);
    },
  });

  useEffect(() => {
    if (hasHydrated || !job) return;
    const hydrated: JobEditRequirementsValues = {
      ageMin: job.ageMin !== null && job.ageMin !== undefined ? String(job.ageMin) : '',
      ageMax: job.ageMax !== null && job.ageMax !== undefined ? String(job.ageMax) : '',
      selectedCompetencias: (job.competencias ?? []).map((s) =>
        toSelectedSkill({ name: s.name, level: s.level as ProficiencyLevel })
      ),
      selectedDiferenciais: (job.diferenciais ?? []).map((s) =>
        toSelectedSkill({ name: s.name, level: s.level as ProficiencyLevel })
      ),
      selectedDiversity: [...(job.diversityCriteria ?? [])] as DiversityCriterionId[],
      disabilityDetails: job.disabilityDetails
        ? {
            selectedIds: [...job.disabilityDetails.selectedIds],
            otherText: job.disabilityDetails.otherText,
            label: buildDisabilityPillLabel(
              job.disabilityDetails.selectedIds,
              job.disabilityDetails.otherText
            ),
          }
        : null,
    };
    form.setFieldValue('ageMin', hydrated.ageMin);
    form.setFieldValue('ageMax', hydrated.ageMax);
    form.setFieldValue('selectedCompetencias', hydrated.selectedCompetencias);
    form.setFieldValue('selectedDiferenciais', hydrated.selectedDiferenciais);
    form.setFieldValue('selectedDiversity', hydrated.selectedDiversity);
    form.setFieldValue('disabilityDetails', hydrated.disabilityDetails);
    setHasHydrated(true);
  }, [job, hasHydrated, form]);

  useEffect(() => {
    if (!jobQuery.isError) return;
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status === 404) {
      toast('Vaga não encontrada.');
      void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH });
    }
  }, [jobQuery.isError, jobQuery.error, navigate]);

  const values = useStore(form.store, (state) => state.values);
  const fieldMeta = useStore(form.store, (state) => state.fieldMeta);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const rawErrors = useFormErrors(form);

  // Filter por isDefaultValue: erros só aparecem em campos modificados.
  // Mesma técnica de EU-160 — evita o bug de "setFieldValue programático
  // marca isDirty=true mesmo quando value === default".
  //
  // Erros de idade vêm de `computeAgeErrors(values, contractType)` em vez do
  // `superRefine` do Zod, porque a regra RN-03 (`jovem-aprendiz` → max 24)
  // depende de um valor EXTERNO ao form (vem da subseção Condições de
  // Trabalho). Zod só consegue olhar o próprio shape; injetamos os erros
  // computados de fora aqui, e a regra dispara mesmo após o consultor editar
  // o `contractType` em outra subseção (`setQueryData` atualiza o cache).
  // RN-03 também aparece em campos pristine se aplicável — não filtramos
  // ageMin/ageMax via `isDefaultValue` porque a invariante "max > 24 com
  // contrato Aprendiz" precisa ser visível imediatamente após hidratação.
  const errors = useMemo(() => {
    const filtered: Record<string, string> = {};
    for (const [field, msg] of Object.entries(rawErrors)) {
      if (field === 'ageMin' || field === 'ageMax') continue; // computamos abaixo
      const meta = (fieldMeta as Record<string, { isDefaultValue?: boolean } | undefined>)[field];
      if (meta && meta.isDefaultValue === false) filtered[field] = msg;
    }
    const ageErrors = computeAgeErrors(values, contractType);
    if (ageErrors.ageMin) filtered.ageMin = ageErrors.ageMin;
    if (ageErrors.ageMax) filtered.ageMax = ageErrors.ageMax;
    return filtered;
  }, [rawErrors, fieldMeta, values, contractType]);

  // Bloqueia o submit enquanto houver qualquer erro de idade ativo (RN-02,
  // RN-03 ou `ageMax ≤ ageMin`). Sem isso, o `buildPayload` apenas omite os
  // campos de idade e os demais campos seguem salvando — bug reportado pela
  // QA em que as mensagens apareciam mas o save passava silenciosamente.
  const canSubmit = !errors.ageMin && !errors.ageMax;

  function handleAgeChange(field: 'ageMin' | 'ageMax', value: string) {
    form.setFieldValue(field, value);
  }

  function handlePresetClick(field: 'competencia' | 'diferencial', presetLabel: string) {
    const list =
      field === 'competencia' ? values.selectedCompetencias : values.selectedDiferenciais;
    const existing = list.find((skill) => skill.name.toLowerCase() === presetLabel.toLowerCase());
    if (existing) {
      // Toggle off — remove direto.
      handleSkillRemove(field, existing.id);
      return;
    }
    // Cap 50 (FR-092): bloquear adição se a lista do field já tem 50 entradas.
    if (list.length >= MAX_SKILLS_PER_FIELD) {
      const setter = field === 'competencia' ? setCompetenciaCapError : setDiferencialCapError;
      setter(
        `Você atingiu o limite de ${MAX_SKILLS_PER_FIELD} ${field === 'competencia' ? 'competências' : 'diferenciais'} por vaga.`
      );
      return;
    }
    setSkillDialogMode({ kind: 'select-level', presetLabel, field });
    setSkillDialogOpen(true);
  }

  function handleCustomSkillClick(field: 'competencia' | 'diferencial') {
    const list =
      field === 'competencia' ? values.selectedCompetencias : values.selectedDiferenciais;
    if (list.length >= MAX_SKILLS_PER_FIELD) {
      const setter = field === 'competencia' ? setCompetenciaCapError : setDiferencialCapError;
      setter(
        `Você atingiu o limite de ${MAX_SKILLS_PER_FIELD} ${field === 'competencia' ? 'competências' : 'diferenciais'} por vaga.`
      );
      return;
    }
    setSkillDialogMode({ kind: 'create', field });
    setSkillDialogOpen(true);
  }

  function handleSkillRemove(field: 'competencia' | 'diferencial', skillId: string) {
    const key = field === 'competencia' ? 'selectedCompetencias' : 'selectedDiferenciais';
    const next = (values[key] as SelectedSkill[]).filter((skill) => skill.id !== skillId);
    form.setFieldValue(key, next);
    // Remover sempre limpa o cap error daquele field.
    const setter = field === 'competencia' ? setCompetenciaCapError : setDiferencialCapError;
    setter(undefined);
  }

  function handleSkillSubmit(submission: SkillSubmission) {
    if (!skillDialogMode) return;
    const key =
      skillDialogMode.field === 'competencia' ? 'selectedCompetencias' : 'selectedDiferenciais';
    const setterCap =
      skillDialogMode.field === 'competencia' ? setCompetenciaCapError : setDiferencialCapError;
    const skill: SelectedSkill = {
      id: submission.isCustom
        ? `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        : `preset-${submission.name.toLowerCase()}`,
      name: submission.name,
      level: submission.level,
      label: `${submission.name} - ${levelLabel(submission.level)}`,
    };
    const current = values[key] as SelectedSkill[];
    // Dedup case-insensitive por nome (substitui se já existir).
    const filtered = current.filter(
      (existing) => existing.name.toLowerCase() !== skill.name.toLowerCase()
    );
    const next = [...filtered, skill];
    if (next.length > MAX_SKILLS_PER_FIELD) {
      setterCap(
        `Você atingiu o limite de ${MAX_SKILLS_PER_FIELD} ${skillDialogMode.field === 'competencia' ? 'competências' : 'diferenciais'} por vaga.`
      );
      setSkillDialogOpen(false);
      setSkillDialogMode(null);
      return;
    }
    form.setFieldValue(key, next);
    setterCap(undefined);
    setSkillDialogOpen(false);
    toast(toastTitleForSkill(skillDialogMode, submission.isCustom), {
      action: { label: 'Fechar', onClick: () => undefined },
    });
    setSkillDialogMode(null);
  }

  function handleDiversityToggle(id: DiversityCriterionId) {
    if (id === 'pessoas-com-deficiencia') {
      const isSelected = values.selectedDiversity.includes(id);
      if (isSelected) {
        // Remoção direta — sem reabrir modal. Limpa disabilityDetails.
        form.setFieldValue(
          'selectedDiversity',
          values.selectedDiversity.filter((entry) => entry !== id)
        );
        form.setFieldValue('disabilityDetails', null);
        return;
      }
      // Não-selecionada: abrir modal antes de adicionar.
      setDisabilityDialogOpen(true);
      return;
    }
    const next = values.selectedDiversity.includes(id)
      ? values.selectedDiversity.filter((entry) => entry !== id)
      : [...values.selectedDiversity, id];
    form.setFieldValue('selectedDiversity', next);
  }

  function handleDisabilitySubmit(value: DisabilitySelection) {
    const details: SelectedDisabilityCriterion = {
      selectedIds: value.selectedIds,
      otherText: value.otherText,
      label: buildDisabilityPillLabel(value.selectedIds, value.otherText),
    };
    const currentDiversity = values.selectedDiversity;
    const nextDiversity: DiversityCriterionId[] = currentDiversity.includes(
      'pessoas-com-deficiencia'
    )
      ? currentDiversity
      : [...currentDiversity, 'pessoas-com-deficiencia'];
    form.setFieldValue('selectedDiversity', nextDiversity);
    form.setFieldValue('disabilityDetails', details);
    setDisabilityDialogOpen(false);
    toast('Critério de diversidade adicionado', {
      action: { label: 'Fechar', onClick: () => undefined },
    });
  }

  function handleBack() {
    void navigate({
      to: '/vagas/$jobId',
      params: { jobId },
    });
  }

  // Loading state.
  if (jobQuery.isLoading) {
    return <JobEditRequirementsLoadingFallback />;
  }

  // Erro de carregamento (≠ 404).
  if (jobQuery.isError) {
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status !== 404) {
      return <JobEditRequirementsErrorFallback onRetry={() => void jobQuery.refetch()} />;
    }
  }

  return (
    <>
      <JobEditRequirementsPageComponent
        jobName={jobName}
        companyLogoUrl={companyLogoUrl}
        companyName={companyName}
        values={values}
        errors={errors}
        isSubmitting={isSubmitting || patchJob.isPending}
        canSubmit={canSubmit}
        readOnly={readOnly}
        competenciaPresets={COMPETENCIA_PRESETS}
        diferencialPresets={DIFERENCIAL_PRESETS}
        competenciaCapError={competenciaCapError}
        diferencialCapError={diferencialCapError}
        onAgeChange={handleAgeChange}
        onPresetClick={handlePresetClick}
        onCustomSkillClick={handleCustomSkillClick}
        onSkillRemove={handleSkillRemove}
        onDiversityToggle={handleDiversityToggle}
        onBack={handleBack}
        onSubmit={() => {
          // EU-569 — modo visualização: nada persiste.
          if (readOnly) return;
          // Belt-and-suspenders: o botão já está desabilitado quando
          // `canSubmit === false`, mas Enter no campo numérico ainda
          // dispara form submit nativo — recusamos aqui também.
          if (!canSubmit) return;
          void form.handleSubmit();
        }}
      />
      <SkillDialog
        open={skillDialogOpen}
        onOpenChange={(open) => {
          setSkillDialogOpen(open);
          if (!open) setSkillDialogMode(null);
        }}
        mode={skillDialogMode}
        onSubmit={handleSkillSubmit}
      />
      <DisabilityDialog
        open={disabilityDialogOpen}
        onOpenChange={setDisabilityDialogOpen}
        defaultValue={
          values.disabilityDetails
            ? {
                selectedIds: values.disabilityDetails.selectedIds,
                otherText: values.disabilityDetails.otherText,
              }
            : undefined
        }
        onSubmit={handleDisabilitySubmit}
      />
    </>
  );
}

function toastTitleForSkill(mode: SkillDialogMode, isCustom: boolean): string {
  if (mode.field === 'competencia') {
    return isCustom ? 'Competência adicionada' : 'Domínio de competência adicionado';
  }
  return isCustom ? 'Diferencial adicionado' : 'Domínio de diferencial adicionado';
}
